import { useEffect, useMemo, useState } from "react";
import { Loader2, LogOut, RefreshCw, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const STATUSES = ["new", "replied", "archived"];

const Login = () => {
  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto max-w-md px-6 py-28" data-testid="dashboard-login">
      <p className="font-mono-label mb-5">Private area</p>
      <h1 className="text-4xl font-extrabold leading-[1.05]">Dashboard</h1>
      <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
        Sign in with Google to review inquiries and booked calls.
      </p>
      <button
        onClick={signIn}
        data-testid="google-signin-button"
        className="mt-8 w-full rounded-sm bg-[#FF4D00] px-6 py-3 text-sm font-medium text-white transition-transform hover:-translate-y-0.5"
      >
        Continue with Google
      </button>
    </div>
  );
};

const statusPill = (s) =>
  s === "new"
    ? "border-[#FF4D00] text-[#FF4D00]"
    : s === "replied"
    ? "border-foreground text-foreground"
    : "border-border text-muted-foreground";

const InquiryRow = ({ inquiry, onUpdate }) => {
  const [note, setNote] = useState(inquiry.note || "");
  const [busy, setBusy] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await http.patch(`/inquiries/${inquiry.inquiry_id}`, body);
      onUpdate(data);
      toast.success("Inquiry updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-border p-5" data-testid={`inquiry-row-${inquiry.inquiry_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold">{inquiry.name}</h3>
          <p className="font-mono-label mt-1 normal-case tracking-normal">
            {inquiry.email} · {inquiry.project_type} · {inquiry.budget_range}
          </p>
        </div>
        <span
          className={`rounded-sm border px-2 py-1 text-[11px] uppercase tracking-widest ${statusPill(inquiry.status)}`}
          data-testid={`inquiry-status-${inquiry.inquiry_id}`}
        >
          {inquiry.status}
        </span>
      </div>

      <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-foreground/80">{inquiry.message}</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            disabled={busy || inquiry.status === s}
            onClick={() => patch({ status: s })}
            data-testid={`set-status-${s}-${inquiry.inquiry_id}`}
            className="rounded-sm border border-border px-3 py-1.5 text-xs capitalize transition-colors hover:border-[#FF4D00] hover:text-[#FF4D00] disabled:opacity-40"
          >
            Mark {s}
          </button>
        ))}
        <span className="font-mono-label ml-auto">{new Date(inquiry.created_at).toLocaleString()}</span>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Private note"
          data-testid={`note-input-${inquiry.inquiry_id}`}
          className="flex-1 rounded-sm border border-input bg-background px-3 py-2 text-sm outline-none focus:border-[#FF4D00]"
        />
        <button
          disabled={busy}
          onClick={() => patch({ note })}
          data-testid={`save-note-${inquiry.inquiry_id}`}
          className="rounded-sm border border-foreground px-4 py-2 text-xs transition-colors hover:bg-foreground hover:text-background disabled:opacity-40"
        >
          Save note
        </button>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [i, b] = await Promise.all([http.get("/inquiries"), http.get("/bookings")]);
      setInquiries(i.data);
      setBookings(b.data);
    } catch {
      toast.error("Could not load dashboard data");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const shown = useMemo(
    () => (filter === "all" ? inquiries : inquiries.filter((i) => i.status === filter)),
    [inquiries, filter]
  );

  if (loading) {
    return (
      <div className="flex items-center gap-3 p-16 font-mono-label" data-testid="dashboard-loading">
        <Loader2 size={16} className="animate-spin" /> Checking session…
      </div>
    );
  }
  if (!user) return <Login />;

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: inquiries.filter((i) => i.status === s).length }), {});

  return (
    <div className="mx-auto max-w-6xl px-4 pt-10 sm:px-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="font-mono-label mb-2">Signed in as {user.email}</p>
          <h1 className="text-3xl font-extrabold sm:text-4xl">Inbox</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="dashboard-refresh"
            className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-xs hover:border-foreground"
          >
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="dashboard-logout"
            className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-xs hover:border-[#FF4D00] hover:text-[#FF4D00]"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 divide-x divide-border border-b border-border sm:grid-cols-4">
        {[["Total", inquiries.length], ["New", counts.new], ["Replied", counts.replied], ["Archived", counts.archived]].map(
          ([label, n]) => (
            <div key={label} className="px-4 py-5">
              <p className="text-xl font-bold">{n ?? 0}</p>
              <p className="font-mono-label mt-1">{label}</p>
            </div>
          )
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 py-5" data-testid="dashboard-status-filter">
        <span className="font-mono-label mr-2">Filter</span>
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`dashboard-filter-${s}`}
            className={`rounded-sm border px-3 py-1.5 text-xs capitalize transition-colors ${
              filter === s ? "border-[#FF4D00] bg-[#FF4D00] text-white" : "border-border hover:border-foreground"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="border border-border bg-card" data-testid="dashboard-inquiry-list">
        {shown.length === 0 ? (
          <p className="p-10 text-sm text-muted-foreground" data-testid="dashboard-empty">
            No inquiries here yet.
          </p>
        ) : (
          shown.map((i) => (
            <InquiryRow
              key={i.inquiry_id}
              inquiry={i}
              onUpdate={(u) => setInquiries((prev) => prev.map((p) => (p.inquiry_id === u.inquiry_id ? u : p)))}
            />
          ))
        )}
      </div>

      <section className="mt-14 pb-20" data-testid="dashboard-bookings">
        <div className="mb-5 flex items-center gap-3">
          <CalendarClock size={16} className="text-[#FF4D00]" />
          <p className="font-mono-label">Booked calls</p>
        </div>
        <div className="divide-y divide-border border border-border bg-card">
          {bookings.length === 0 ? (
            <p className="p-8 text-sm text-muted-foreground" data-testid="dashboard-bookings-empty">
              No calls booked yet.
            </p>
          ) : (
            bookings.map((b) => (
              <div key={b.booking_id} className="flex flex-wrap items-center justify-between gap-2 p-5" data-testid={`booking-row-${b.booking_id}`}>
                <div>
                  <p className="text-sm font-bold">{b.name}</p>
                  <p className="font-mono-label mt-1 normal-case tracking-normal">
                    {b.email}{b.topic ? ` · ${b.topic}` : ""}
                  </p>
                </div>
                <p className="font-mono-label">{b.slot_date} · {b.slot_time} ET</p>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
